import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { agentsAPI, payoutAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign,
  CreditCard,
  Receipt,
  TrendingUp,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Calendar,
  Building,
  Wallet,
  Send,
  Loader2,
  Users,
  Percent,
  CheckCircle2,
  Calculator,
  Globe,
  PoundSterling,
  Info,
  FileText
} from 'lucide-react';

const FinancesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [agentProfile, setAgentProfile] = useState(null);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    bank_details: '',
    notes: ''
  });
  const [submittingPayout, setSubmittingPayout] = useState(false);
  
  // Commission Calculator State
  const [calculatorData, setCalculatorData] = useState({
    tuitionFee: 13000,
    destination: 'UK',
    applicationFee: 0
  });

  // Commission Constants
  const COMMISSION_RATE = 0.20; // 20% of tuition
  const AGENT_SHARE = 0.70; // 70%
  const COMPANY_SHARE = 0.30; // 30%
  const AVG_TUITION = 13000;

  // Mock financial data for students
  const financialSummary = {
    totalFees: 15000,
    paidAmount: 5000,
    pendingAmount: 10000,
    currency: 'GBP'
  };

  const transactions = [
    { id: 1, type: 'payment', description: 'Application Fee - De Montfort University', amount: 150, date: '2024-12-15', status: 'completed' },
    { id: 2, type: 'payment', description: 'Deposit - MSc Computer Science', amount: 4850, date: '2024-12-10', status: 'completed' },
    { id: 3, type: 'invoice', description: 'Tuition Fee - Semester 1', amount: 10000, date: '2025-01-15', status: 'pending' },
  ];

  useEffect(() => {
    if (isAuthenticated && user?.role === 'agent') {
      fetchAgentData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchAgentData = async () => {
    try {
      const [agentRes, payoutsRes] = await Promise.all([
        agentsAPI.getMe(),
        payoutAPI.getMyRequests().catch(() => ({ data: [] }))
      ]);
      setAgentProfile(agentRes.data);
      setPayoutRequests(payoutsRes.data || []);
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payoutForm.amount);
    
    if (isNaN(amount) || amount < 50) {
      toast.error('Minimum payout amount is £50');
      return;
    }

    if (amount > (agentProfile?.commission_pending || 0)) {
      toast.error('Amount exceeds available balance');
      return;
    }

    setSubmittingPayout(true);
    try {
      await payoutAPI.request({
        amount,
        bank_details: payoutForm.bank_details,
        notes: payoutForm.notes
      });
      toast.success('Payout request submitted!');
      setShowPayoutDialog(false);
      setPayoutForm({ amount: '', bank_details: '', notes: '' });
      fetchAgentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payout request');
    } finally {
      setSubmittingPayout(false);
    }
  };

  const getPayoutStatusColor = (status) => {
    const colors = {
      pending: 'bg-teal-100 text-teal-600',
      approved: 'bg-blue-100 text-blue-600',
      paid: 'bg-green-100 text-green-600',
      rejected: 'bg-red-100 text-red-600'
    };
    return colors[status] || colors.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  // Calculate commission based on inputs
  const calculateCommission = () => {
    const tuition = parseFloat(calculatorData.tuitionFee) || 0;
    const appFee = parseFloat(calculatorData.applicationFee) || 0;
    
    const totalCommission = tuition * COMMISSION_RATE;
    const agentCommission = totalCommission * AGENT_SHARE;
    const companyCommission = totalCommission * COMPANY_SHARE;
    
    return {
      totalCommission,
      agentCommission,
      companyCommission,
      applicationFee: appFee
    };
  };

  const commissionCalc = calculateCommission();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-teal-600/10 flex items-center justify-center mx-auto mb-6">
              <DollarSign className="w-10 h-10 text-teal-600" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-800 mb-4">Manage Your Finances</h1>
            <p className="text-gray-500 mb-8">
              Sign in to view your payments, invoices, and financial summary
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/login')}
                className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
              >
                Sign In
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Create Account
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Agent Commission View
  if (user?.role === 'agent') {
    if (loading) {
      return (
        <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
        <Navbar />
        
        <main className="pt-20 pb-12">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-100/80 via-[#e9ecef] to-[#f1f3f5] py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-teal-600/20 text-teal-600">Agent</Badge>
                <Badge className="bg-green-100 text-green-600">
                  <Percent className="w-3 h-3 mr-1" />
                  Competitive Commission
                </Badge>
              </div>
              <h1 className="text-4xl font-semibold text-gray-800 mb-2">Commission &amp; Earnings</h1>
              <p className="text-gray-600 dark:text-gray-300">Track your referral earnings and request payouts</p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Commission Summary Cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Total Earned</span>
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-teal-600">{formatCurrency(agentProfile?.total_commission)}</p>
                <p className="text-sm text-gray-500 mt-1">Lifetime earnings</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Available Balance</span>
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-green-600">{formatCurrency(agentProfile?.commission_pending)}</p>
                <p className="text-sm text-gray-500 mt-1">Ready to withdraw</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Paid Out</span>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-gray-800 dark:text-white">{formatCurrency(agentProfile?.commission_paid)}</p>
                <p className="text-sm text-gray-500 mt-1">Successfully withdrawn</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Total Referrals</span>
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-gray-800 dark:text-white">{agentProfile?.total_referrals || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Students referred</p>
              </div>
            </div>

            {/* Commission Calculator */}
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Commission Calculator</h2>
                    <p className="text-gray-500 text-sm">Calculate your earnings per student placement</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Calculator Inputs */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-600 dark:text-gray-300">Destination Country</Label>
                    <Select 
                      value={calculatorData.destination} 
                      onValueChange={(v) => setCalculatorData({
                        ...calculatorData, 
                        destination: v,
                        applicationFee: v === 'UK' ? 0 : calculatorData.applicationFee
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-800 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        <SelectItem value="UK">United Kingdom (No App Fee)</SelectItem>
                        <SelectItem value="USA">United States</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="Australia">Australia</SelectItem>
                        <SelectItem value="Ireland">Ireland</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {calculatorData.destination === 'UK' && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <Info className="w-3 h-3" /> UK universities do not charge application fees
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-gray-600 dark:text-gray-300">Tuition Fee (£)</Label>
                    <Input
                      type="number"
                      value={calculatorData.tuitionFee}
                      onChange={(e) => setCalculatorData({...calculatorData, tuitionFee: e.target.value})}
                      placeholder="13000"
                      className="bg-white border-gray-200 text-gray-800 mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Average UK tuition: £13,000</p>
                  </div>

                  {calculatorData.destination !== 'UK' && (
                    <div>
                      <Label className="text-gray-600 dark:text-gray-300">Application Fee (£) - Optional</Label>
                      <Input
                        type="number"
                        value={calculatorData.applicationFee}
                        onChange={(e) => setCalculatorData({...calculatorData, applicationFee: e.target.value})}
                        placeholder="0"
                        className="bg-white border-gray-200 text-gray-800 mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Add if applicable for this destination</p>
                    </div>
                  )}
                </div>

                {/* Calculator Results */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-teal-600" />
                    Your Earnings Breakdown
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-500 dark:text-gray-400">Total Commission (20%)</span>
                      <span className="text-gray-800 font-medium">{formatCurrency(commissionCalc.totalCommission)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-green-600 font-semibold">Your Commission</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Agent share</p>
                      </div>
                      <span className="text-2xl font-semibold text-green-600">{formatCurrency(commissionCalc.agentCommission)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">H E Direct</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Company share</p>
                      </div>
                      <span className="text-gray-600 dark:text-gray-300">{formatCurrency(commissionCalc.companyCommission)}</span>
                    </div>

                    {calculatorData.applicationFee > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400">+ Application Fee</span>
                        <span className="text-gray-600 dark:text-gray-300">{formatCurrency(calculatorData.applicationFee)}</span>
                      </div>
                    )}

                    <div className="mt-4 p-3 bg-teal-500/10 border border-yellow-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-teal-600">
                        <Wallet className="w-4 h-4" />
                        <span className="font-medium">Per Student Earnings</span>
                      </div>
                      <p className="text-2xl font-semibold text-gray-800 mt-1">
                        {formatCurrency(commissionCalc.agentCommission + (parseFloat(calculatorData.applicationFee) || 0))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Payout Section */}
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Request Payout</h2>
                  <p className="text-gray-500 text-sm">Minimum withdrawal: £50</p>
                </div>
                {(agentProfile?.commission_pending || 0) >= 50 && (
                  <Button
                    onClick={() => setShowPayoutDialog(true)}
                    className="bg-green-600 hover:bg-green-500 text-white font-semibold"
                    data-testid="request-payout-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Request Payout
                  </Button>
                )}
              </div>

              {(agentProfile?.commission_pending || 0) < 50 && (
                <div className="p-4 bg-teal-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-teal-600" />
                    <p className="text-amber-700 text-sm">
                      Minimum balance of £50 required to request a payout. Current balance: {formatCurrency(agentProfile?.commission_pending)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Payout History */}
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Payout History</h2>
              
              {payoutRequests.length > 0 ? (
                <div className="space-y-4">
                  {payoutRequests.map((payout) => (
                    <div 
                      key={payout.id}
                      className="flex items-center justify-between p-4 bg-[#e9ecef]/50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          payout.status === 'paid' ? 'bg-green-100' : 
                          payout.status === 'approved' ? 'bg-blue-100' :
                          payout.status === 'rejected' ? 'bg-red-100' : 'bg-teal-100'
                        }`}>
                          {payout.status === 'paid' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : payout.status === 'rejected' ? (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-teal-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">Payout Request</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(payout.requested_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-800 dark:text-white">{formatCurrency(payout.amount)}</p>
                        <Badge className={getPayoutStatusColor(payout.status)}>
                          {payout.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-4">
                    <Receipt className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">No payout requests yet</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your payout history will appear here</p>
                </div>
              )}
            </div>

            {/* Commission Structure - Updated */}
            <div className="mt-8 p-6 bg-gradient-to-r from-teal-50 to-[#f1f3f5] rounded-xl border border-gray-200">
              <h3 className="text-gray-800 font-semibold mb-4">How Commission Works</h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                  <div className="text-2xl font-semibold text-teal-600 mb-1">20%</div>
                  <p className="text-gray-600 text-sm">Commission Rate</p>
                  <p className="text-gray-500 text-xs">Of course tuition fee</p>
                </div>
                <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                  <div className="text-xl font-semibold text-green-600 mb-1">Tiered</div>
                  <p className="text-gray-600 text-sm">Your Share</p>
                  <p className="text-gray-500 text-xs">Earn more as you grow</p>
                </div>
                <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                  <div className="text-2xl font-semibold text-gray-800 mb-1">£{AVG_TUITION.toLocaleString()}</div>
                  <p className="text-gray-600 text-sm">Avg. Tuition</p>
                  <p className="text-gray-500 text-xs">UK universities</p>
                </div>
                <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                  <div className="text-2xl font-semibold text-green-600 mb-1">3-5 Days</div>
                  <p className="text-gray-600 text-sm">Processing Time</p>
                  <p className="text-gray-500 text-xs">For payout requests</p>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-blue-700 font-medium">Application Fees</p>
                    <p className="text-gray-500 text-sm">
                      UK universities do not charge application fees. For other destinations (USA, Canada, Australia, etc.), 
                      application fees vary and can be added manually to your commission calculation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Payout Request Dialog */}
        <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
          <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-800 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-teal-600" />
                Request Payout
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePayoutSubmit} className="space-y-4 mt-4">
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <p className="text-gray-500 text-sm mb-1">Available Balance</p>
                <p className="text-2xl font-semibold text-green-600">{formatCurrency(agentProfile?.commission_pending)}</p>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-300">Amount (£) *</Label>
                <Input
                  type="number"
                  min="50"
                  max={agentProfile?.commission_pending || 0}
                  step="0.01"
                  value={payoutForm.amount}
                  onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                  placeholder="Minimum £50"
                  className="bg-white border-gray-200 text-gray-800 mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-300">Bank Details *</Label>
                <Textarea
                  value={payoutForm.bank_details}
                  onChange={(e) => setPayoutForm({ ...payoutForm, bank_details: e.target.value })}
                  placeholder="Account Name, Sort Code, Account Number, Bank Name"
                  className="bg-white border-gray-200 text-gray-800 mt-1"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-300">Notes (Optional)</Label>
                <Textarea
                  value={payoutForm.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  placeholder="Any additional information..."
                  className="bg-white border-gray-200 text-gray-800 mt-1"
                  rows={2}
                />
              </div>

              <Button
                type="submit"
                disabled={submittingPayout}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold"
              >
                {submittingPayout ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Student View
  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-100/80 via-[#e9ecef] to-[#f1f3f5] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-semibold text-gray-800 mb-2">Finances</h1>
            <p className="text-gray-600 dark:text-gray-300">Manage your payments and view financial summary</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Summary Cards - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Financial Summary</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Your payment overview</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Total Fees</span>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-gray-800 dark:text-white">{formatCurrency(financialSummary.totalFees)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">For current academic year</p>
              </div>

              <div className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Paid Amount</span>
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-green-600 dark:text-green-400">{formatCurrency(financialSummary.paidAmount)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Successfully processed</p>
              </div>

              <div className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 dark:text-gray-400">Pending Amount</span>
                  <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                </div>
                <p className="text-3xl font-semibold text-teal-600 dark:text-teal-400">{formatCurrency(financialSummary.pendingAmount)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Due for payment</p>
              </div>
            </div>
          </div>

          {/* Payment Options - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Payment Options</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose your preferred payment method</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border-2 border-gray-200 dark:border-slate-600 hover:border-teal-400 transition-all cursor-pointer">
                <CreditCard className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Card Payment</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pay securely with credit or debit card</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border-2 border-gray-200 dark:border-slate-600 hover:border-teal-400 transition-all cursor-pointer">
                <Building className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Bank Transfer</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Direct transfer to university account</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border-2 border-gray-200 dark:border-slate-600 hover:border-teal-400 transition-all cursor-pointer">
                <TrendingUp className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Payment Plan</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Split fees into installments</p>
              </div>
            </div>
          </div>

          {/* Transactions - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Transaction History</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your recent payments</p>
                </div>
              </div>
              <Button variant="outline" className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>

            <div className="space-y-4">
              {transactions.map((tx) => (
                <div 
                  key={tx.id}
                  className="flex items-center justify-between p-4 bg-[#e9ecef]/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      tx.status === 'completed' ? 'bg-green-100' : 'bg-teal-100'
                    }`}>
                      {tx.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-teal-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-gray-800 font-medium">{tx.description}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(tx.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-semibold ${tx.status === 'completed' ? 'text-gray-800' : 'text-teal-600'}`}>
                      {formatCurrency(tx.amount)}
                    </p>
                    <Badge className={tx.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-teal-100 text-teal-600'}>
                      {tx.status === 'completed' ? 'Paid' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 p-6 bg-gradient-to-r from-teal-50 to-[#f1f3f5] rounded-xl border border-gray-200">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-gray-800 font-medium mb-1">Need Help with Payments?</h3>
                <p className="text-gray-500 text-sm mb-3">
                  Our finance team is here to help you with payment plans, scholarships, and any financial queries.
                </p>
                <Link to="/help">
                  <Button variant="outline" className="border-teal-400 text-teal-600 hover:bg-teal-600/10">
                    Contact Finance Team
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FinancesPage;
